import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Envelope, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";

/**
 * Public unsubscribe landing — referenced by the List-Unsubscribe header in every
 * Brevo email we send. Provides a friendly one-click opt-out and surfaces the
 * SMS STOP keyword for completeness.
 */
export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await axios.post(`${BACKEND_URL}/api/email-opt-outs`, { email });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not record the unsubscribe — please reply to any MedexOVA email with the word STOP.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-[80vh] bg-bone">
      <div className="max-w-xl mx-auto px-6 lg:px-8 py-20">
        <Link to="/" className="text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-ink inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Back to MedexOVA
        </Link>
        <div className="mt-6 rounded-2xl border border-[#E2E0D8] bg-white p-8">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-brandblue">
            <Envelope size={12} weight="fill" /> Email preferences
          </div>
          <h1 className="mt-3 font-heading text-3xl font-black text-ink">Unsubscribe from MedexOVA emails</h1>
          {!done ? (
            <>
              <p className="mt-3 text-sm text-slate_navy/80 leading-relaxed">
                You'll stop receiving non-essential email from MedexOVA. <b>Trip-critical messages</b>
                (driver dispatched, on-the-way, arrived, completed) will still go through so we
                don't strand anyone — those are operational, not marketing.
              </p>
              <form onSubmit={submit} className="mt-6 space-y-3" data-testid="unsubscribe-form">
                <label className="block text-xs uppercase tracking-[0.2em] font-bold text-slate_navy">Email address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="unsubscribe-email-input"
                  className="w-full rounded-md border border-[#E2E0D8] bg-white px-4 py-3 text-sm text-slate_navy focus:outline-none focus:ring-2 focus:ring-brandblue/20 focus:border-brandblue"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="unsubscribe-submit-btn"
                  className="inline-flex items-center gap-2 rounded-md bg-brandblue-ink hover:bg-brandblue-deep disabled:opacity-60 text-white px-5 py-3 text-sm font-semibold transition-colors"
                >
                  {submitting ? "Saving…" : "Unsubscribe"}
                </button>
                {err && <p className="text-xs text-red-600">{err}</p>}
              </form>
              <hr className="my-6 border-[#E2E0D8]" />
              <p className="text-xs text-slate-500">
                To stop SMS messages from us, simply reply <b>STOP</b> to any of our texts.
                You can also reach a human at <a className="text-brandblue underline" href="tel:+18336592699">(833) 659-2699</a>.
              </p>
            </>
          ) : (
            <div className="mt-6 rounded-lg border border-forest/30 bg-forest/5 p-5" data-testid="unsubscribe-success">
              <div className="inline-flex items-center gap-2 text-forest font-bold">
                <CheckCircle size={18} weight="fill" /> You're unsubscribed
              </div>
              <p className="mt-2 text-sm text-slate_navy">
                We've added <b>{email}</b> to our opt-out list. Trip-critical operational
                alerts will still reach you so we never leave a rider in the dark.
              </p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}
